import React from "react";
import { useContactModal } from "../context/ContactModalContext";
import PageHeader from "../components/PageHeader";
import ImageTile from "../components/ImageTile";
import { heroImages, aboutImages } from "../data/images";

const features = [
  { icon: "🛡️", title: "Quality Without Compromise", body: "Every lot passes multi-stage inspection — visual, moisture, pesticide residue, and microbial testing — before it leaves our facility.", image: aboutImages.warehouse },
  { icon: "🚢", title: "Logistics Built for Distance", body: "Reefer containers, humidity control, and vacuum-sealed packaging preserve freshness across the longest trade lanes.", image: aboutImages.shipping },
  { icon: "📄", title: "Documentation, Handled", body: "FSSAI, APEDA, phytosanitary, certificate of origin, and EUR.1 — we take the paperwork off your desk.", image: heroImages.spiceMarket },
  { icon: "🌾", title: "Farmer-First Sourcing", body: "Direct relationships with growers mean consistent volume, fairer prices, and full visibility into the supply chain.", image: aboutImages.farmer },
  { icon: "🧊", title: "Uninterrupted Cold Chain", body: "Pre-cooling facilities and temperature-monitored transit from farm gate to destination port.", image: heroImages.basket },
  { icon: "🎯", title: "Custom-Spec Orders", body: "Tell us size grade, packaging, labeling, and MOQ — we build the order around your retail requirements.", image: heroImages.harvestHands },
];

const certs = ["FSSAI Registered", "APEDA Member", "Phytosanitary Certified", "HACCP Compliant", "ISO Packaging", "GlobalG.A.P. Aligned"];

export default function WhyUsPage() {
  const { openModal } = useContactModal();

  return (
    <>
      <PageHeader eyebrow="Why Myriad Green Energy"
        title="Built for the standards" titleAccent="of international buyers."
        subtitle="From documentation to temperature, every link in the chain is engineered for the realities of global trade — and for the expectations of premium retail."
        image={aboutImages.warehouse} emoji="🛡️"
        rightContent={
          <div className="glass-strong shimmer-border rounded-3xl p-6 w-full max-w-sm">
            <div className="text-[11px] uppercase tracking-[0.22em] text-stone-400 mb-4">Certifications &amp; Compliance</div>
            <ul className="space-y-3">
              {["FSSAI Registered","APEDA Member","HACCP Compliant","ISO 22000 Packaging","GlobalG.A.P. Aligned","Phytosanitary Certified"].map(c => (
                <li key={c} className="flex items-center gap-3">
                  <span className="w-5 h-5 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shrink-0">
                    <svg viewBox="0 0 12 12" className="w-3 h-3 text-white" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2 6l3 3 5-5" />
                    </svg>
                  </span>
                  <span className="text-sm text-stone-300">{c}</span>
                </li>
              ))}
            </ul>
          </div>
        } />

      {/* Features */}
      <section className="relative py-24 md:py-32">
        <div className="w-full px-4 sm:px-6 lg:px-10 max-w-400 mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f, i) => (
              <div key={f.title} className="group glass rounded-3xl overflow-hidden glass-hover fade-up" style={{ animationDelay: `${i * 0.07}s` }}>
                <ImageTile src={f.image} alt={f.title} emoji={f.icon} className="aspect-16/10"
                  imgClassName="group-hover:scale-110"
                  overlayClassName="bg-gradient-to-t from-black/80 via-black/20 to-transparent">
                  <div className="absolute top-3 left-3 w-11 h-11 rounded-xl glass-strong flex items-center justify-center text-xl">{f.icon}</div>
                </ImageTile>
                <div className="p-7">
                  <h3 className="font-display text-xl font-medium text-stone-700">{f.title}</h3>
                  <p className="text-sm text-stone-400 mt-2 leading-relaxed">{f.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certs */}
      <section className="relative py-20">
        <div className="w-full px-4 sm:px-6 lg:px-10 max-w-400 mx-auto">
          <div className="glass-strong rounded-3xl p-8 md:p-10">
            <div className="text-[11px] uppercase tracking-[0.22em] text-stone-400 mb-4">Certifications &amp; Compliance</div>
            <div className="flex flex-wrap gap-2">
              {certs.map(c => (
                <span key={c} className="glass-pill rounded-full px-4 py-2 text-sm text-stone-700">{c}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Key numbers */}
      <section className="relative py-20 md:py-28">
        <div className="w-full px-4 sm:px-6 lg:px-10 max-w-400 mx-auto">
          <div className="max-w-2xl mb-12">
            <div className="inline-flex items-center gap-2 glass-pill rounded-full px-4 py-1.5 mb-5">
              <span className="text-[11px] uppercase tracking-[0.22em] text-emerald-700">Why International Buyers Choose Myriad</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-semibold leading-tight text-stone-900">
              The numbers behind <span className="text-gradient">the promise.</span>
            </h2>
          </div>

          <div className="glass-strong shimmer-border noise rounded-3xl p-6 md:p-10 grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            {[
              { v: "1000+", l: "Partner Farms" },
              { v: "360°",  l: "Supply Chain Control" },
              { v: "100%",  l: "Traceable Origin" },
              { v: "24/7",  l: "Buyer Support" },
            ].map((s, i) => (
              <div key={s.l} className={`text-center ${i !== 0 ? "md:border-l md:border-stone-900/10" : ""} px-2`}>
                <div className="font-display text-4xl md:text-5xl font-semibold text-gradient">{s.v}</div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-stone-500 mt-1.5">{s.l}</div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <button onClick={openModal}
              className="inline-flex items-center gap-2 px-7 py-4 rounded-2xl text-sm font-medium text-white bg-linear-to-br from-emerald-700 via-emerald-600 to-teal-700 hover:from-emerald-600 hover:to-emerald-700 transition shadow-xl shadow-emerald-600/25">
              Partner With Us
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
